// eslint-disable-next-line import/no-unresolved, import/no-extraneous-dependencies
import {
	ColumnFilterOptions,
	ColumnFilterValue,
	Pagination,
	ResolvedResponse,
	Sort2 as Sort,
	Status,
	Translations,
} from '@innovyze/lib-am-common';
import { createAction } from '@reduxjs/toolkit';
import {
	CreateConfig,
	InitRiskMatrixPreviewPayload,
	LogReply,
	RiskCalcStatusResult,
	RiskConfig,
	SimulationData,
	RiskGradeRanges,
	RiskMatrixGridObj,
	RiskMatrixObj,
	TaskData,
	PositionObj,
	OverallRiskSetting,
	RiskConfigNameCheck,
} from '@Types/risk.types';
// eslint-disable-next-line import/no-unresolved, import/no-extraneous-dependencies
import { paginationInitialState } from '@innovyze/lib-am-common/Reducers';
import { addNamespace } from '@Utils/actions';
import { ConfigPublishState } from '@Types/riskConfig.types';
import {
	ApplyRiskTemplateRequest,
	ComponentAvailability,
	TemplateApplyStatus,
} from '@Types/riskTemplateApply.types';
import {
	ComponentCount,
	ComponentMetadata,
	MultiParamCell,
} from '@Types/component.types';
import {
	IsCountLoadingChange,
	RiskCountChange,
	RiskCountRename,
} from '@Reducers/Risk.reducers';
import { CoverageEntry } from '@Types/riskCoverage.types';
import { RiskResultsSummary } from '@Types/riskResultsSummary.types';
import { RiskBreakdownResponse } from '@Types/riskBreakdown.types';

export const paginationDefault = (): Pagination => ({
	...paginationInitialState,
	sortKey: 'CURRENT_RISK',
	sortDescending: true,
});

export const getRiskConfig = createAction<string>('Risk/getRiskConfig');
export const getRiskConfigResolved = createAction<RiskConfig>(
	'Risk/getRiskConfigResolved',
);
export const getRiskConfigRejected = createAction('Risk/getRiskConfigRejected');
export const clearRiskConfig = createAction('Risk/clearRiskConfig');

export const applyRiskTemplate = createAction<{
	history: { push: (path: string) => void };
	page: number;
	templateLabel: string;
	config: ApplyRiskTemplateRequest;
}>('Risk/applyRiskTemplate');

export const setTemplateApplyStatus = createAction<TemplateApplyStatus>(
	'Risk/setTemplateApplyStatus',
);

export const setTemplateApplyResult = createAction<{
	availability: ComponentAvailability[];
	unrecognisedValues: { componentName: string; values: string[] }[];
	templateLabel: string;
}>('Risk/setTemplateApplyResult');

export const getRiskConfigCoverage = createAction<string>(
	'Risk/getRiskConfigCoverage',
);
export const getRiskConfigCoverageResolved = createAction<CoverageEntry[]>(
	'Risk/getRiskConfigCoverageResolved',
);
export const getRiskConfigCoverageRejected = createAction(
	'Risk/getRiskConfigCoverageRejected',
);
export const getRiskResultsSummary = createAction<string>(
	'Risk/getRiskResultsSummary',
);
export const getRiskResultsSummaryResolved = createAction<{
	configId: string;
	data: RiskResultsSummary;
}>('Risk/getRiskResultsSummaryResolved');
export const getRiskResultsSummaryRejected = createAction<string>(
	'Risk/getRiskResultsSummaryRejected',
);
/** requestId identifies this fetch so the reducer can drop a stale response. */
export const getRiskBreakdown = createAction<{
	configId: string;
	assetId: string;
	requestId: number;
}>('Risk/getRiskBreakdown');
export const getRiskBreakdownResolved = createAction<{
	configId: string;
	assetId: string;
	requestId: number;
	data: RiskBreakdownResponse;
}>('Risk/getRiskBreakdownResolved');
export const getRiskBreakdownRejected = createAction<{
	configId: string;
	assetId: string;
	requestId: number;
}>('Risk/getRiskBreakdownRejected');
/** A run finished successfully for this config (carried explicitly, not read
 *  from whichever config happens to be loaded when the status arrives). */
export const recordRunCompleted = createAction<string>(
	'Risk/recordRunCompleted',
);

export const getRiskAssetCount = createAction<{
	assetType: string;
	assetFilterId?: string;
}>('Risk/getRiskAssetCount');
export const getRiskAssetCountResolved = createAction<number>(
	'Risk/getRiskAssetCountResolved',
);
export const getRiskAssetCountRejected = createAction(
	'Risk/getRiskAssetCountRejected',
);

export const createRiskConfig = createAction<{
	history: unknown;
	config: CreateConfig;
	page: number;
	isRiskTemplatesEnabled: boolean;
}>('Risk/createRiskConfig');

export const changeRiskConfigPublishState = createAction<{
	newPublishState: ConfigPublishState;
	configId: string;
	refresh: () => void;
}>('Risk/changeRiskConfigPublishState');

export const duplicateRiskConfig = createAction<{
	history: unknown;
	config?: {
		name: string;
		assetFilterId?: string;
	};
	configId: string;
	isRiskTemplatesEnabled: boolean;
}>('Risk/duplicateRiskConfig');

export const setRiskConfigIsCreating = createAction<boolean>(
	'riskSetup/setRiskConfigIsCreating',
);

export const setRiskConfigNameError = createAction<string>(
	'riskSetup/setRiskConfigNameError',
);

// payload: the trimmed name, exactly as create would send it
export const checkRiskConfigName = createAction<string>(
	'riskSetup/checkRiskConfigName',
);

export const setRiskConfigNameCheck = createAction<RiskConfigNameCheck>(
	'riskSetup/setRiskConfigNameCheck',
);

export const renameRiskConfigAction = createAction<RiskConfig>(
	'Risk/renameRiskConfig',
);

export const saveRiskConfigAction = createAction<RiskConfig>(
	'Risk/saveRiskConfig',
);

export const getRiskDetails = createAction<Pagination>('risk/getRiskDetails');

export const runRisk = createAction<string>('risk/runRisk');
export const runRiskRejected = createAction('risk/runRiskRejected');
export const runRiskResolved = createAction('risk/runRiskResolved');

export const resetRisk = createAction<string>('risk/resetRisk');
export const resetRiskRejected = createAction('risk/resetRiskRejected');
export const resetRiskResolved = createAction('risk/resetRiskResolved');

export const getRiskDetailsDefaulted = () =>
	getRiskDetails(paginationDefault());
export const reloadRiskDetails = createAction<string>('risk/reloadRiskDetails');
export const getRiskDetailsWSReseived = createAction<{
	searchStr: string;
	response: ResolvedResponse;
}>('riskdetails/getRiskDetails/WSReseived');
export const getRiskDetailsResolved = createAction<ResolvedResponse>(
	'riskdetails/getRiskDetails/resolved',
);
export const getRiskDetailsRejected = createAction<Status>(
	'riskdetails/getRiskDetails/rejected',
);

export const changePagination = createAction<{ page?: number; slice?: number }>(
	'riskdetails/paginationModel',
);

export const clearRiskScanId = createAction(
	addNamespace('riskdetails/clearRiskScanId'),
);

export const changeSlice = createAction<number>('riskdetails/slice');

export const changePage = createAction<number>('riskdetails/page');

export const changeSearch = createAction<string>('riskdetails/changeSearch');
export const setSearchStart = createAction<boolean>(
	'riskdetails/setSearchStart',
);
export const setSearchStartSaga = createAction<boolean>(
	'riskdetails/setSearchStartSaga',
);

export const clearScanId = createAction<string>('riskdetails/clearScanId');

export const changeSort = createAction<Sort>('riskdetails/sort');

export const setSelected = createAction<string[]>('riskdetails/selected/set');

export const addSelected = createAction<string[]>('riskdetails/selected/add');

export const removeSelected = createAction<string[]>(
	'riskdetails/selected/remove',
);

export const clearSelected = createAction('riskdetails/selected/clear');

export const currentRow = createAction<number>('riskdetails/current/row');

export const setRiskDetailsIsBusy = createAction<boolean>('riskdetails/isBusy');
export const setTab = createAction<string>('Risk/setTab');
export const setSaveRun = createAction<boolean>('Risk/setSaveRun');

export const setRiskCalcStatus = createAction<RiskCalcStatusResult>(
	'Risk/setRiskCalcStatus',
);

export const getLog = createAction<string>('Log/getLog');
export const getLogResolved = createAction<LogReply>('Log/getLogResolved');
export const getLogRejected = createAction('Log/getLogRejected');
export const getSimulationData = createAction('Risk/getSimulationData');
export const getSimulationDataResolved = createAction<SimulationData>(
	'Risk/getSimulationDataResolved',
);

export const getTaskData = createAction<string>('Risk/getTaskData');
export const getTaskDataResolved = createAction<TaskData>(
	'Risk/getTaskDataResolved',
);

export const changeFilterSelect = createAction<ColumnFilterValue>(
	addNamespace('riskDetails/filterSelect'),
);
export const changeFiltersSelect = createAction<ColumnFilterValue[]>(
	addNamespace('riskDetails/filtersSelect'),
);

export const updateColumnFilterSelectOptions =
	createAction<ColumnFilterOptions>(
		addNamespace('riskDetails/filterSelect/column/options'),
	);

export const translate = createAction<Translations>(
	addNamespace('riskDetails/translate'),
);

export const setRiskSetupDirty = createAction<boolean>(
	'riskSetup/setRiskSetupDirty',
);

export const setRunCompleteReload = createAction<boolean>(
	'Risk/riskCompleteReload',
);

export const setRefreshList = createAction<boolean>('Risk/setRefreshList');

export const setRiskIsSaving = createAction<boolean>(
	'riskSetup/setRiskIsSaving',
);

export const deleteRisk = createAction<{
	configId: string;
	refresh: () => void;
}>('rehab/deleteRisk');
export const setRiskIsDeleting = createAction<boolean>(
	'risk/setRiskIsDeleting',
);

export const setRiskCountIsLoading = createAction<IsCountLoadingChange>(
	'Risk/setRiskCountIsLoading',
);

export const getRiskCountInspection = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	assetFilterId?: string;
	fieldName: string;
	inspectionType: string;
}>('Risk/getRiskCountInspection');

export const getRiskCountAttribute = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	assetFilterId?: string;
	fieldName: string;
}>('Risk/getRiskCountAttribute');

export const getRiskCountDeteriorationModel = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	assetFilterId?: string;
	fieldName: string;
	deteriorationModelId: string | null;
}>('Risk/getRiskCountDeteriorationModel');

export const getRiskCountFailureDefinition = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	assetFilterId?: string;
	fieldName: string;
	failureDefinitionId: string | null;
}>('Risk/getRiskCountFailureDefinition');

export const getRiskCountObservation = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	assetFilterId?: string;
	fieldName: string;
}>('Risk/getRiskCountObservation');

export const getRiskCountProximity = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	assetFilterId?: string;
	layerName: string;
}>('Risk/getRiskCountProximity');

export const getRiskCountProximityAttribute = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	assetFilterId?: string;
	layerName: string;
	fieldName: string;
	valueType: string;
	distance: number;
}>('Risk/getRiskCountProximityAttribute');

export const getRiskCountCustomTable = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	assetFilterId?: string;
	fieldName: string;
	tableName: string;
	valueType: string;
}>('Risk/getRiskCountCustomTable');

export const getRiskCountTask = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	assetFilterId?: string;
	fieldName: string;
	tableName: string;
	valueType: string;
	inspectionType: string;
}>('Risk/getRiskCountTask');

export const getRiskCountScoreMatrix = createAction<{
	key: string;
	components: ComponentCount[];
	scoreMatrix: MultiParamCell[];
	assetType: string;
	assetFilterId?: string;
	inspectionType?: string;
}>('Risk/getRiskCountScoreMatrix');

export const getRiskCountResolved = createAction<RiskCountChange>(
	'Risk/getRiskCountResolved',
);

export const renameRiskCount = createAction<RiskCountRename>(
	'Risk/renameRiskCount',
);

//=== risk Matrix actions =====
export const setUpdatedRanges = createAction<RiskGradeRanges>(
	'Risk/setUpdatedRanges',
);
export const setDidMatrixUpdate = createAction<boolean>(
	'Risk/setDidMatrixUpdate',
);
export const setUpdatedScores = createAction<RiskMatrixGridObj[]>(
	'Risk/setUpdatedScores',
);
export const setRiskType = createAction<string>('Risk/setRiskType'); //switch between linear and matrix

export const initRiskMatrixPreview = createAction<InitRiskMatrixPreviewPayload>(
	'Risk/initRiskMatrixPreview',
);
export const cancelRiskMatrixPreview = createAction<{
	configId: string;
	matrix: RiskMatrixObj;
}>('Risk/cancelRiskMatrixPreview');
export const getRiskMatrixPreview = createAction<{
	configId: string;
	initialLoad?: boolean;
	cb?: (value: string) => void;
}>('Risk/getRiskMatrixPreview');
export const setEnablePreview = createAction<boolean>('Risk/setEnablePreview');
export const setCancellPreview = createAction<boolean>(
	'Risk/setCancellPreview',
);
export const setIsPreviewLoading = createAction<boolean>(
	'Risk/setIsPreviewLoading',
);
export const setIsPreviewOnLoad = createAction<boolean>(
	'Risk/setIsPreviewOnLoad',
);
export const setMatrixPreviewStatus = createAction<string>(
	'Risk/setmatrixPreviewStatus',
);
export const setRiskMatrixAfterPreview = createAction<RiskMatrixObj>(
	'Risk/setRiskMatrixAfterPreview',
);
export const setRefreshMatrixGrid = createAction<boolean>(
	'Risk/setRefreshMatrixGrid',
);
export const setEditedCellsList = createAction<PositionObj>(
	'Risk/updateEditedCellsList',
);
export const setOnlyGradesUpdated = createAction<boolean>(
	'Risk/setOnlyGradesUpdated',
);

export const clearMatrixUpdate = createAction<boolean>(
	'Risk/clearMatrixUpdate',
);

export const setOverallRisk = createAction<OverallRiskSetting>(
	'Risk/setOverallRisk',
);

//===================
